import { lazy } from 'react';
import { Switch, Route, Redirect, BrowserRouter } from 'react-router-dom';
import { BaseProvider, Environment } from '@increasecard/increase-core';
import config, {
  environment as configEnvironment,
  productName,
} from './config';
import { SubscriptionsScreen } from './features/subscription/SubscriptionsScreen';
import { PaymentsScreen } from './features/payments/PaymentsScreen';
import { CustomersScreen } from './features/customers/CustomersScreen';
import { PrivateRoute } from './PrivateRoute';
import { PublicRoute } from './PublicRoute';
import { SettingsScreen } from './features/settings/SettingsScreen';
import { RedirectToSandbox } from './components/common/RedirectToSandbox';
import {
  PayAccessControl,
  PayAuthorizations,
} from './features/authorizations/PayAccessControl';
import { PublicQueryProvider } from './services/QueryProvider';
import { AppHeader } from './AppHeader';
import { useNavigationTracking } from './hooks/useNavigationTracking';
import { ToastBar, Toaster } from 'react-hot-toast';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { featureFlags } from './featureFlags';
import { useInitErrorTracker } from './utils/errorTracker';
import { useLoadAppcues } from './hooks/useLoadAppcues';
import { ClearingsList } from './features/clearings/ClearingsList';
import { PaymentLinksList } from './features/paymentLink/PaymentLinksList';
import { PaymentLinkCreateScreen } from './features/paymentLink/PaymentLinkCreateScreen';
import { useIsAggregator } from './hooks/useIsAggregator';
const SummaryScreen = lazy(() => import('./features/summary/SummaryScreen'));
const DebtPortalScreen = lazy(
  () => import('./features/checkout/debtPortal/DebtPortalScreen')
);
const CheckoutLite = lazy(() => import('./features/checkout/CheckoutLite'));
const InvoiceCheckoutScreen = lazy(
  () => import('./features/checkout/invoice/InvoiceCheckoutScreen')
);
const CustomerPaymentMethodScreen = lazy(
  () => import('./features/checkout/CustomerPaymentMethodScreen')
);
const CheckoutScreen = lazy(() => import('./features/checkout/CheckoutScreen'));
const ProductsPlansScreen = lazy(
  () => import('./features/plan/ProductsPlansScreen')
);
const ExtrasScreen = lazy(() => import('./features/extras/ExtrasScreen'));

export function increaseCoreEnvironmentFromConfigEnvironment(
  configEnvironment: string
): Environment {
  switch (configEnvironment) {
    case 'sandbox': {
      return 'production';
    }
    case 'production': {
      return 'production';
    }
    case 'staging': {
      return 'staging';
    }
    case 'playground': {
      return 'playground';
    }
    default: {
      throw new Error(`Invalid config environment ${configEnvironment}`);
    }
  }
}
interface PublicRoutesProps {
  NoMatchRoutes: () => JSX.Element;
}

function PublicRoutes({ NoMatchRoutes }: PublicRoutesProps): JSX.Element {
  return (
    <PublicQueryProvider>
      <ReactQueryDevtools />
      <Switch>
        <PublicRoute exact path="/checkout/:account_id">
          <CheckoutScreen />
        </PublicRoute>
        <PublicRoute exact path="/invoice/:account_id">
          <InvoiceCheckoutScreen />
        </PublicRoute>
        <PublicRoute exact path="/payment-method/:account_id">
          <CustomerPaymentMethodScreen />
        </PublicRoute>
        <PublicRoute exact path="/checkout-lite/:account_id">
          <CheckoutLite />
        </PublicRoute>
        <PublicRoute exact path="/selfservice-portal/:account_id">
          <DebtPortalScreen />
        </PublicRoute>
        <Route path="*">
          <NoMatchRoutes />
        </Route>
      </Switch>
    </PublicQueryProvider>
  );
}

function PrivateRoutes() {
  useNavigationTracking();
  useInitErrorTracker();
  useLoadAppcues();
  const isAggregator = useIsAggregator();

  const defaultPath = isAggregator ? '/invoices' : '/subscriptions';
  return (
    <AppHeader>
      <Switch>
        <Redirect exact from="/" to={defaultPath} />
        <PrivateRoute exact path="/summary">
          <PayAccessControl allowed={PayAuthorizations.viewDashboard}>
            <SummaryScreen />
          </PayAccessControl>
        </PrivateRoute>
        <Route exact path="/invoices/clearings">
          <PayAccessControl allowed={PayAuthorizations.viewClearings}>
            <ClearingsList />
          </PayAccessControl>
        </Route>
        <PrivateRoute exact path="/invoices/payment-links">
          <PaymentLinksList />
        </PrivateRoute>
        <PrivateRoute exact path="/payment-links/new">
          <PaymentLinkCreateScreen />
        </PrivateRoute>
        <PrivateRoute path="/subscriptions">
          <SubscriptionsScreen />
        </PrivateRoute>

        <PrivateRoute path={['/invoices', '/payments']}>
          <PaymentsScreen />
        </PrivateRoute>

        <PrivateRoute path="/customers">
          <CustomersScreen />
        </PrivateRoute>

        <PrivateRoute path={['/plans', '/products']}>
          <ProductsPlansScreen />
        </PrivateRoute>

        <PrivateRoute path="/extras">
          <ExtrasScreen />
        </PrivateRoute>

        <PrivateRoute path="/settings">
          <SettingsScreen />
        </PrivateRoute>
      </Switch>
    </AppHeader>
  );
}

function PrivateRoutesContainer() {
  const { eventTracking, errorTracking } = config;

  const environment =
    increaseCoreEnvironmentFromConfigEnvironment(configEnvironment);

  const trackingCfg = {
    ...eventTracking,
    enabled: featureFlags.enableTracking,
  };

  return (
    <BaseProvider
      settings={{
        environment,
        productName,
        eventTracking: trackingCfg,
        errorTracking,
      }}
    >
      <RedirectToSandbox />
      <PrivateRoutes />
      <Toaster position="top-center" containerStyle={{ top: '60px' }}>
        {(t) => (
          <ToastBar
            toast={t}
            style={{
              ...t.style,
              animation: t.visible
                ? 'custom-slide-in-top 0.35s cubic-bezier(0.21, 1.02, 0.43, 1.01) forwards'
                : 'custom-slide-out-top 0.4s cubic-bezier(0.06, 0.71, 0.55, 1) forwards',
            }}
          />
        )}
      </Toaster>
    </BaseProvider>
  );
}

export function AppRoutes(): JSX.Element {
  return (
    <BrowserRouter>
      <PublicRoutes NoMatchRoutes={PrivateRoutesContainer} />
    </BrowserRouter>
  );
}
